import React from 'react';
import { ExternalLink, Mail, MessageCircle, Mic, Paperclip, RefreshCw, Send, Square, UserCheck, X } from 'lucide-react';
import { whatsappService, WhatsAppQueueItem, WhatsAppThreadDetail } from '../services/whatsappService';
import { userService } from '../services/userService';
import { Role, UserAccount } from '../types';
import { minutesSince, whatsappMessagePreview } from '../utils/inboxHubHelpers';
import { MAX_ATTACHMENT_SIZE_BYTES, allowedAttachment } from './InboxHub/whatsapp/whatsAppPanelHelpers';

type Props = {
  currentUser: UserAccount | null;
  currentRole: Role;
  activeTab?: string;
  suppressBackgroundWhatsApp?: boolean;
  onOpenFullHub?: (threadId?: string) => void;
};

const leadershipRoles = new Set<Role>(['ADMIN', 'CEO', 'DFO', 'CP']);

const parseTime = (value?: string | null): number => {
  const ms = new Date(String(value || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const sortByUrgency = (items: WhatsAppQueueItem[]): WhatsAppQueueItem[] => {
  const bucket = (item: WhatsAppQueueItem) => {
    if (item.isOverdue && item.needsReply) return 0;
    if (item.needsReply) return 1;
    if (item.isOverdue) return 2;
    return 3;
  };
  return [...items].sort((a, b) => {
    const byBucket = bucket(a) - bucket(b);
    if (byBucket !== 0) return byBucket;
    return parseTime(b.lastMessageAt || b.lastInboundAt || b.lastOutboundAt) - parseTime(a.lastMessageAt || a.lastInboundAt || a.lastOutboundAt);
  });
};

const getMessageAt = (message: any): string => String(message?.sentAt || message?.receivedAt || message?.createdAt || '');
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const QUICK_DRAWER_THREAD_MESSAGE_LIMIT = 80;
const QUICK_DRAWER_THREAD_FAILURE_COOLDOWN_MS = 30000;
const QUICK_DRAWER_QUEUE_POLL_MS = 20000;
const WA_INBOX_ACTIVE_WINDOW_KEY = '__MAOS_WA_INBOX_ACTIVE__';
const WA_MAIN_ACTIVE_WINDOW_KEY = '__MAOS_WHATSAPP_MAIN_ACTIVE';

const renderWhatsAppFormattedText = (text: string) => {
  const segments = text.split(/(`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g);
  return segments.map((segment, index) => {
    if (!segment) return null;
    const key = `${index}-${segment.slice(0, 8)}`;
    if (segment.startsWith('*') && segment.endsWith('*') && segment.length > 2) {
      return <strong key={key}>{segment.slice(1, -1)}</strong>;
    }
    if (segment.startsWith('_') && segment.endsWith('_') && segment.length > 2) {
      return <em key={key}>{segment.slice(1, -1)}</em>;
    }
    if (segment.startsWith('~') && segment.endsWith('~') && segment.length > 2) {
      return <span key={key} className="line-through">{segment.slice(1, -1)}</span>;
    }
    if (segment.startsWith('`') && segment.endsWith('`') && segment.length > 2) {
      return <code key={key} className="rounded bg-black/10 px-1 py-0.5 text-[0.92em]">{segment.slice(1, -1)}</code>;
    }
    return <React.Fragment key={key}>{segment}</React.Fragment>;
  });
};

const getThreadDisplayName = (thread?: Partial<WhatsAppQueueItem & WhatsAppThreadDetail> | null) => {
  return String(
    thread?.groupName ||
    thread?.customerName ||
    thread?.customerPhone ||
    thread?.customerJid ||
    'Selected thread'
  );
};

const mediaKind = (message: any): 'image' | 'video' | 'audio' | 'document' | null => {
  const messageType = String(message?.messageType || '').toUpperCase();
  const mimeType = String(message?.mediaMimeType || message?.MediaAsset?.mimeType || '').toLowerCase();
  const fileName = String(message?.mediaFileName || message?.MediaAsset?.fileName || '').toLowerCase();
  if (mimeType.startsWith('image/') || messageType === 'IMAGE') return 'image';
  if (mimeType.startsWith('video/') || messageType === 'VIDEO') return 'video';
  if (mimeType.startsWith('audio/') || messageType === 'AUDIO') return 'audio';
  if (messageType === 'DOCUMENT' || fileName.endsWith('.pdf')) return 'document';
  return null;
};

const normalizeDigits = (value?: string | null) => String(value || '').replace(/\D/g, '');
const isLidJid = (value?: string | null) => String(value || '').trim().toLowerCase().endsWith('@lid');
const normalizePhoneLabel = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw || isLidJid(raw)) return '';
  const digits = normalizeDigits(raw);
  return digits.length >= 7 && digits.length <= 15 ? digits : '';
};
const isHumanLabel = (value?: string | null) => {
  const label = String(value || '').trim();
  if (!label || /^\d+$/.test(label)) return false;
  return /[A-Za-z\u00C0-\u024F]/.test(label);
};

type QuickMediaPreviewItem = {
  url: string;
  kind: 'image' | 'video' | 'audio' | 'document';
  title: string;
};

const QuickMediaPreview: React.FC<{
  message: NonNullable<WhatsAppThreadDetail['Messages']>[number];
  outbound: boolean;
  onOpenMedia: (item: QuickMediaPreviewItem) => void;
}> = ({ message, outbound, onOpenMedia }) => {
  const kind = mediaKind(message);
  const url = String(message.mediaUrl || '').trim();
  const fileName = String(message.mediaFileName || message.MediaAsset?.fileName || '').trim();
  const label = fileName || whatsappMessagePreview(message);

  if (kind === 'image' && url) {
    return (
      <button type="button" onClick={() => onOpenMedia({ url, kind, title: fileName || 'WhatsApp image' })} className="mb-1 block overflow-hidden rounded-xl border border-black/10 bg-black/5 text-left">
        <img src={url} alt={fileName || 'WhatsApp image'} className="max-h-44 w-full object-cover" loading="lazy" />
      </button>
    );
  }

  if (kind === 'video' && url) {
    return <video src={url} controls className="mb-1 max-h-44 w-full rounded-xl bg-black" />;
  }

  if (kind === 'audio' && url) {
    return <audio src={url} controls className="mb-1 w-full max-w-[260px]" />;
  }

  if (kind || url) {
    return (
      <a
        href={url || undefined}
        target="_blank"
        rel="noreferrer"
        className={`mb-1 inline-flex max-w-full items-center rounded-xl border px-3 py-2 text-xs font-bold ${
          outbound ? 'border-emerald-200/70 bg-emerald-500/20 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'
        } ${url ? '' : 'pointer-events-none opacity-70'}`}
      >
        <span className="truncate">{url ? label : `${label} unavailable`}</span>
      </a>
    );
  }

  return null;
};

const fileToBase64 = async (file: File): Promise<string> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  return String(dataUrl.split(',')[1] || '');
};

const isMainWhatsAppInboxActive = () =>
  typeof window !== 'undefined' &&
  (Boolean((window as any)[WA_INBOX_ACTIVE_WINDOW_KEY]) || Boolean((window as any)[WA_MAIN_ACTIVE_WINDOW_KEY]));

const isWhatsAppDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return String(window.localStorage.getItem('MAOS_DEBUG_WA') || '').trim() === '1';
  } catch {
    return false;
  }
};

export const GlobalWhatsAppQuickDrawer: React.FC<Props> = ({
  currentUser,
  currentRole,
  activeTab,
  suppressBackgroundWhatsApp = false,
  onOpenFullHub,
}) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState<WhatsAppQueueItem[]>([]);
  const [unreadInboundTotal, setUnreadInboundTotal] = React.useState(0);
  const [selectedThreadId, setSelectedThreadId] = React.useState('');
  const [detail, setDetail] = React.useState<WhatsAppThreadDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [olderLoading, setOlderLoading] = React.useState(false);
  const [reply, setReply] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [pulse, setPulse] = React.useState(false);
  const [replyToMessageId, setReplyToMessageId] = React.useState('');
  const [mentionedJids, setMentionedJids] = React.useState<string[]>([]);
  const [composerCaretPosition, setComposerCaretPosition] = React.useState(-1);
  const [mentionActiveIndex, setMentionActiveIndex] = React.useState(0);
  const [attachment, setAttachment] = React.useState<{ fileName: string; mimeType: string; base64Data: string; fileSizeBytes: number } | null>(null);
  const [mediaPreview, setMediaPreview] = React.useState<QuickMediaPreviewItem | null>(null);
  const [waAssignableUsers, setWaAssignableUsers] = React.useState<Array<{ id: string; name: string }>>([]);
  const [transferUserId, setTransferUserId] = React.useState('');
  const [statusBusy, setStatusBusy] = React.useState(false);
  const [transferBusy, setTransferBusy] = React.useState(false);
  const [sseConnected, setSseConnected] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [recordingSeconds, setRecordingSeconds] = React.useState(0);
  const previousNeedsReplyRef = React.useRef(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const mentionLabelsRef = React.useRef<Record<string, string>>({});
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const mediaStreamRef = React.useRef<MediaStream | null>(null);
  const mediaChunksRef = React.useRef<BlobPart[]>([]);
  const recordingTimerRef = React.useRef<number | null>(null);
  const sseRef = React.useRef<EventSource | null>(null);
  const sseRefreshTimerRef = React.useRef<number | null>(null);
  const sseReconnectTimerRef = React.useRef<number | null>(null);
  const sseReconnectCountRef = React.useRef(0);
  const restoringDraftRef = React.useRef(false);
  const timelineRef = React.useRef<HTMLDivElement | null>(null);
  const timelineNearBottomRef = React.useRef(true);
  const forceScrollToBottomRef = React.useRef(false);
  const selectedThreadIdRef = React.useRef('');
  const loadQueueRef = React.useRef<() => Promise<void>>(async () => undefined);
  const openDetailRef = React.useRef<(threadId: string, silent?: boolean) => Promise<void>>(async () => undefined);
  const detailFailureRef = React.useRef<{ threadId: string; statusCode: number; failedAt: number } | null>(null);
  const preserveScrollAfterPrependRef = React.useRef<{ height: number; top: number } | null>(null);
  const manualUnreadGuardRef = React.useRef<Record<string, number>>({});
  const detailRef = React.useRef<WhatsAppThreadDetail | null>(null);
  const queueInFlightRef = React.useRef(false);

  const currentUserId = String(currentUser?.id || '').trim();
  const isLeadership = leadershipRoles.has(currentRole);
  const isMainWhatsAppOpen = React.useCallback(
    () => suppressBackgroundWhatsApp || isMainWhatsAppInboxActive() || activeTab === 'whatsapp',
    [activeTab, suppressBackgroundWhatsApp],
  );
  const logQuickDrawerSuppressed = React.useCallback((reason: string) => {
    if (!isWhatsAppDebugEnabled()) return;
    console.debug('[WA_OBS][QUICK_DRAWER_SUPPRESSED]', { reason });
  }, []);
  const draftStorageKey = React.useMemo(() => `ma_wa_quick_drafts:${currentUserId || 'anonymous'}`, [currentUserId]);
  const mutedThreadsStorageKey = React.useMemo(() => `ma_whatsapp_muted_threads:${currentUserId || 'anonymous'}`, [currentUserId]);

  const getMutedThreadSet = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(mutedThreadsStorageKey);
      if (!raw) return new Set<string>();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<string>();
      return new Set(parsed.map(id => String(id || '').trim()).filter(Boolean));
    } catch {
      return new Set<string>();
    }
  }, [mutedThreadsStorageKey]);

  const loadQueue = React.useCallback(async (options?: { force?: boolean }) => {
    if (!currentUserId) return;
    if (isMainWhatsAppOpen()) {
      logQuickDrawerSuppressed('main-whatsapp-active');
      return;
    }
    if (queueInFlightRef.current) return;
    try {
      queueInFlightRef.current = true;
      setBusy(true);
      setError('');
      const queueParams: Parameters<typeof whatsappService.getQueue>[0] = {
        needsReply: true,
        limit: 40,
        page: 1,
        includeUnreadCounts: false,
      };
      const result = await whatsappService.getQueue(queueParams);
      const muted = getMutedThreadSet();
      const unmutedScopedItems = (Array.isArray(result.items) ? result.items : []).filter(item => {
        const itemId = String(item?.id || '').trim();
        if (!itemId || muted.has(itemId)) return false;
        return String(item?.ownerUserId || '').trim() === currentUserId || String(item?.assignedUserId || '').trim() === currentUserId;
      });
      const nextItems = sortByUrgency(unmutedScopedItems);
      setItems(nextItems);
      setUnreadInboundTotal(
        nextItems.reduce((sum, item) => sum + Math.max(0, Number(item.unreadInboundCount || 0)), 0)
      );
      setSelectedThreadId(prev => {
        // Keep the currently selected thread stable even when it falls out of the
        // "needsReply" filter after an outbound send. This prevents the mini chat
        // workspace from collapsing to an empty state between replies.
        if (prev && nextItems.some(item => item.id === prev)) return prev;
        const isSelectedThreadVisibleToUser =
          !!prev &&
          !!detailRef.current &&
          detailRef.current.id === prev &&
          (
            String(detailRef.current?.Number?.ownerUserId || '') === currentUserId ||
            String(detailRef.current?.assignedUserId || '') === currentUserId
          );
        if (isSelectedThreadVisibleToUser) return prev;
        return nextItems[0]?.id || '';
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load WhatsApp quick queue.');
      setUnreadInboundTotal(0);
    } finally {
      setBusy(false);
      queueInFlightRef.current = false;
    }
  }, [currentUserId, getMutedThreadSet, isMainWhatsAppOpen, logQuickDrawerSuppressed]);

  const loadAssignableUsers = React.useCallback(async () => {
    if (isMainWhatsAppOpen()) {
      logQuickDrawerSuppressed('main-whatsapp-active');
      return;
    }
    if (!isLeadership) return;
    try {
      const users = await userService.getAllUsers();
      const items = Array.isArray(users) ? users : [];
      setWaAssignableUsers(
        items
          .map((user: any) => ({ id: String(user.id || ''), name: String(user.name || user.email || user.id || '') }))
          .filter(user => user.id)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch {
      setWaAssignableUsers([]);
    }
  }, [isLeadership, isMainWhatsAppOpen, logQuickDrawerSuppressed]);

  const openDetail = React.useCallback(async (threadId: string, silent = false) => {
    if (!threadId) {
      setDetail(null);
      return;
    }
    if (isMainWhatsAppOpen()) {
      logQuickDrawerSuppressed('main-whatsapp-active');
      return;
    }
    try {
      if (!silent) setDetailLoading(true);
      const next = await whatsappService.getThreadDetail(threadId, { messageLimit: QUICK_DRAWER_THREAD_MESSAGE_LIMIT });
      detailFailureRef.current = null;
      setDetail(next);

      const lastInbound = [...(next.Messages || [])]
        .filter(message => String(message?.direction || '').toUpperCase() === 'INBOUND')
        .sort((a, b) => parseTime(getMessageAt(a)) - parseTime(getMessageAt(b)))
        .slice(-1)[0];
      const lastMessageId = String(lastInbound?.id || '').trim();
      const lastAt = getMessageAt(lastInbound);
      const shouldMarkRead = !silent;
      const manualUnreadGuarded = !!manualUnreadGuardRef.current[threadId];
      if (shouldMarkRead && !manualUnreadGuarded && lastMessageId && next.lastReadMessageId !== lastMessageId) {
        const selectedUnread = Number(items.find(item => item.id === threadId)?.unreadInboundCount || 0);
        if (selectedUnread > 0) {
          setItems(prev => prev.map(item => item.id === threadId ? { ...item, unreadInboundCount: 0, needsReply: false, isOverdue: false } : item));
          setUnreadInboundTotal(prev => Math.max(0, prev - selectedUnread));
        }
        void whatsappService.markThreadRead(threadId, {
          lastReadAt: lastAt || new Date().toISOString(),
          lastReadMessageId: lastMessageId,
        }).catch(() => undefined);
      }
    } catch (error: any) {
      detailFailureRef.current = {
        threadId,
        statusCode: Number(error?.response?.status || 0),
        failedAt: Date.now(),
      };
      if (!silent) setError('Failed to load WhatsApp thread.');
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, [isMainWhatsAppOpen, items, logQuickDrawerSuppressed]);

  React.useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
    if (selectedThreadId) {
      delete manualUnreadGuardRef.current[selectedThreadId];
    }
  }, [selectedThreadId]);

  React.useEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  React.useEffect(() => {
    loadQueueRef.current = loadQueue;
  }, [loadQueue]);

  React.useEffect(() => {
    openDetailRef.current = openDetail;
  }, [openDetail]);

  React.useEffect(() => {
    if (isMainWhatsAppOpen()) {
      logQuickDrawerSuppressed('main-whatsapp-active');
      return;
    }
    void loadQueue({ force: true });
    void loadAssignableUsers();
  }, [isMainWhatsAppOpen, loadAssignableUsers, loadQueue, logQuickDrawerSuppressed]);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      if (isMainWhatsAppOpen()) return;
      void loadQueue();
    }, sseConnected ? 45000 : QUICK_DRAWER_QUEUE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isMainWhatsAppOpen, loadQueue, sseConnected]);

  React.useEffect(() => {
    if (!open || !selectedThreadId) return;
    forceScrollToBottomRef.current = true;
    void openDetail(selectedThreadId);
  }, [open, openDetail, selectedThreadId]);

  React.useEffect(() => {
    if (!open || !selectedThreadId) return;
    const timer = window.setInterval(() => {
      if (sseConnected) return;
      const failed = detailFailureRef.current;
      if (
        failed &&
        failed.threadId === selectedThreadId &&
        failed.statusCode === 504 &&
        Date.now() - failed.failedAt < QUICK_DRAWER_THREAD_FAILURE_COOLDOWN_MS
      ) {
        return;
      }
      void openDetail(selectedThreadId, true);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [open, openDetail, selectedThreadId, sseConnected]);

  React.useEffect(() => {
    if (!currentUserId) return;
    if (isMainWhatsAppOpen()) {
      logQuickDrawerSuppressed('main-whatsapp-active');
      return;
    }
    const token = String(localStorage.getItem('ma_jwt') || '').trim();
    if (!token) return;
    const apiBase = (import.meta as any)?.env?.VITE_API_URL || '/meos/api';
    let cancelled = false;

    const scheduleRefresh = (event?: Event) => {
      let eventThreadId = '';
      if (event && 'data' in event) {
        try {
          eventThreadId = String((JSON.parse(String((event as MessageEvent).data || '{}')) as any)?.threadId || '').trim();
        } catch {
          eventThreadId = '';
        }
      }
      if (sseRefreshTimerRef.current) window.clearTimeout(sseRefreshTimerRef.current);
      sseRefreshTimerRef.current = window.setTimeout(() => {
        const activeThreadId = String(selectedThreadIdRef.current || '').trim();
        if (!isMainWhatsAppOpen()) {
          void loadQueueRef.current();
        }
        if (!activeThreadId) return;
        if (eventThreadId && eventThreadId !== activeThreadId) return;
        void openDetailRef.current(activeThreadId, true);
      }, 150);
    };

    const connect = () => {
      if (cancelled) return;
      if (isMainWhatsAppOpen()) {
        logQuickDrawerSuppressed('main-whatsapp-active');
        return;
      }
      const source = new EventSource(`${apiBase}/whatsapp/stream?token=${encodeURIComponent(token)}`);
      sseRef.current = source;
      source.addEventListener('thread.updated', scheduleRefresh as EventListener);
      source.addEventListener('open', () => {
        setSseConnected(true);
        sseReconnectCountRef.current = 0;
      });
      source.onerror = () => {
        setSseConnected(false);
        source.close();
        if (sseRef.current === source) sseRef.current = null;
        if (cancelled) return;
        const delay = Math.min(3000 * Math.pow(2, sseReconnectCountRef.current), 30000);
        sseReconnectCountRef.current += 1;
        sseReconnectTimerRef.current = window.setTimeout(() => {
          const activeThreadId = String(selectedThreadIdRef.current || '').trim();
          if (!isMainWhatsAppOpen()) {
            void loadQueueRef.current();
          }
          const failed = detailFailureRef.current;
          if (
            activeThreadId &&
            !(
              failed &&
              failed.threadId === activeThreadId &&
              failed.statusCode === 504 &&
              Date.now() - failed.failedAt < QUICK_DRAWER_THREAD_FAILURE_COOLDOWN_MS
            )
          ) {
            void openDetailRef.current(activeThreadId, true);
          }
          connect();
        }, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      sseRef.current?.removeEventListener('thread.updated', scheduleRefresh as EventListener);
      sseRef.current?.close();
      sseRef.current = null;
      setSseConnected(false);
      if (sseRefreshTimerRef.current) {
        window.clearTimeout(sseRefreshTimerRef.current);
        sseRefreshTimerRef.current = null;
      }
      if (sseReconnectTimerRef.current) {
        window.clearTimeout(sseReconnectTimerRef.current);
        sseReconnectTimerRef.current = null;
      }
    };
  }, [currentUserId, isMainWhatsAppOpen, logQuickDrawerSuppressed]);

  React.useEffect(() => {
    setTransferUserId('');
    setAttachment(null);
    setReplyToMessageId('');
    setMentionedJids([]);
    setComposerCaretPosition(-1);
  }, [selectedThreadId]);

  const stopMediaStream = React.useCallback(() => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop();
      } catch {}
      stopMediaStream();
    };
  }, [stopMediaStream]);

  React.useEffect(() => {
    if (!currentUserId || !selectedThreadId) {
      setReply('');
      setReplyToMessageId('');
      return;
    }
    try {
      const raw = localStorage.getItem(draftStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      const entry = parsed && typeof parsed === 'object' ? parsed[selectedThreadId] : null;
      restoringDraftRef.current = true;
      setReply(String(entry?.reply || ''));
      setReplyToMessageId(String(entry?.replyToMessageId || ''));
      window.setTimeout(() => {
        restoringDraftRef.current = false;
      }, 0);
    } catch {
      restoringDraftRef.current = true;
      setReply('');
      setReplyToMessageId('');
      window.setTimeout(() => {
        restoringDraftRef.current = false;
      }, 0);
    }
  }, [currentUserId, draftStorageKey, selectedThreadId]);

  React.useEffect(() => {
    if (!currentUserId || !selectedThreadId) return;
    if (restoringDraftRef.current) return;
    const timer = window.setTimeout(() => {
      try {
        const raw = localStorage.getItem(draftStorageKey);
        const parsed = raw ? JSON.parse(raw) : {};
        const map = parsed && typeof parsed === 'object' ? { ...parsed } : {};

        // Clean old draft entries to keep storage bounded.
        Object.keys(map).forEach(key => {
          const updatedAt = Number(map[key]?.updatedAt || 0);
          if (!updatedAt || Date.now() - updatedAt > DRAFT_TTL_MS) {
            delete map[key];
          }
        });

        if (!reply.trim() && !replyToMessageId) {
          delete map[selectedThreadId];
        } else {
          map[selectedThreadId] = {
            reply,
            replyToMessageId,
            updatedAt: Date.now(),
          };
        }
        localStorage.setItem(draftStorageKey, JSON.stringify(map));
      } catch {
        // ignore storage errors
      }
    }, 220);
    return () => window.clearTimeout(timer);
  }, [currentUserId, draftStorageKey, reply, replyToMessageId, selectedThreadId]);

  const needsReplyCount = React.useMemo(() => items.filter(item => item.needsReply).length, [items]);
  const displayNeedsReplyCount = React.useMemo(() => {
    if (unreadInboundTotal <= 0) {
      if (!open || !selectedThreadId) return needsReplyCount;
      const selectedNeedsReply = items.find(item => item.id === selectedThreadId)?.needsReply;
      return Math.max(0, needsReplyCount - (selectedNeedsReply ? 1 : 0));
    }
    if (!open || !selectedThreadId) return unreadInboundTotal;
    const selectedUnread = Number(items.find(item => item.id === selectedThreadId)?.unreadInboundCount || 0);
    return Math.max(0, unreadInboundTotal - selectedUnread);
  }, [items, needsReplyCount, open, selectedThreadId, unreadInboundTotal]);

  React.useEffect(() => {
    const previous = previousNeedsReplyRef.current;
    if (needsReplyCount > previous) {
      setPulse(true);
      const timer = window.setTimeout(() => setPulse(false), 1400);
      return () => window.clearTimeout(timer);
    }
    previousNeedsReplyRef.current = needsReplyCount;
  }, [needsReplyCount]);

  React.useEffect(() => {
    previousNeedsReplyRef.current = needsReplyCount;
  }, [needsReplyCount]);

  const orderedMessages = React.useMemo(() => {
    return [...(detail?.Messages || [])]
      .sort((a, b) => parseTime(getMessageAt(a)) - parseTime(getMessageAt(b)));
  }, [detail?.Messages]);

  const mentionCandidates = React.useMemo(() => {
    if (String(detail?.chatType || '').toUpperCase() !== 'GROUP') return [];
    return (detail?.groupParticipants || [])
      .map((participant, index) => {
        const jid = String(participant?.jid || '').trim();
        if (!jid) return null;
        const name = isHumanLabel(participant?.name) ? String(participant?.name || '').trim() : '';
        const phone = normalizePhoneLabel(participant?.phone || participant?.jid);
        const label = name || phone || `Member ${index + 1}`;
        const subtitle = phone || (name ? 'Group member' : 'Phone not available yet');
        return { jid, label, subtitle };
      })
      .filter(Boolean) as Array<{ jid: string; label: string; subtitle: string }>;
  }, [detail?.chatType, detail?.groupParticipants]);

  const mentionQuery = React.useMemo(() => {
    if (composerCaretPosition < 0) return null;
    const beforeCaret = reply.slice(0, composerCaretPosition);
    const match = beforeCaret.match(/(?:^|\s)@([^\s@]*)$/);
    return match ? String(match[1] || '').toLowerCase() : null;
  }, [composerCaretPosition, reply]);

  const mentionSuggestions = React.useMemo(() => {
    if (mentionQuery === null) return [];
    return mentionCandidates
      .filter(candidate => {
        const haystack = `${candidate.label} ${candidate.subtitle}`.toLowerCase();
        return !mentionQuery || haystack.includes(mentionQuery);
      })
      .slice(0, 40);
  }, [mentionCandidates, mentionQuery]);

  const mentionDropdownOpen = mentionSuggestions.length > 0;

  const mentionDisplayByJid = React.useMemo(() => {
    const map = new Map<string, string>();
    mentionCandidates.forEach(candidate => {
      map.set(candidate.jid, candidate.label);
      map.set(String(candidate.jid.split('@')[0] || '').trim(), candidate.label);
    });
    return map;
  }, [mentionCandidates]);

  const resolveMentionText = React.useCallback(
    (value?: string | null) => {
      let next = String(value || '');
      mentionDisplayByJid.forEach((label, key) => {
        if (!key || !label) return;
        next = next.split(`@${key}`).join(`@${label}`);
      });
      return next;
    },
    [mentionDisplayByJid],
  );

  React.useEffect(() => {
    setMentionActiveIndex(0);
  }, [mentionQuery, selectedThreadId]);

  const canLoadOlderMessages = React.useMemo(() => {
    const page = detail?.messagePage;
    return Boolean(page?.hasMoreMessagesBefore && page?.nextBeforeSequenceNumber);
  }, [detail?.messagePage]);

  const loadOlderMessages = React.useCallback(async () => {
    const threadId = String(selectedThreadId || '').trim();
    const page = detail?.messagePage;
    const beforeSequenceNumber = Number(page?.nextBeforeSequenceNumber || page?.oldestLoadedSequenceNumber || NaN);
    if (!threadId || !detail || olderLoading || !page?.hasMoreMessagesBefore || !Number.isFinite(beforeSequenceNumber)) return;
    const container = timelineRef.current;
    preserveScrollAfterPrependRef.current = container
      ? { height: container.scrollHeight, top: container.scrollTop }
      : null;
    try {
      setOlderLoading(true);
      setError('');
      const olderDetail = await whatsappService.getThreadDetail(threadId, {
        messageLimit: QUICK_DRAWER_THREAD_MESSAGE_LIMIT,
        beforeSequenceNumber,
      });
      setDetail(prev => {
        if (!prev || prev.id !== threadId) return olderDetail;
        const seen = new Set<string>();
        const merged = [...(olderDetail.Messages || []), ...(prev.Messages || [])].filter((message: any) => {
          const key = String(message?.id || message?.externalMessageId || '').trim();
          if (!key) return true;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        merged.sort((a: any, b: any) => parseTime(getMessageAt(a)) - parseTime(getMessageAt(b)));
        return {
          ...prev,
          ...olderDetail,
          Messages: merged,
          messagePage: olderDetail.messagePage || prev.messagePage,
        };
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load older WhatsApp messages.');
      preserveScrollAfterPrependRef.current = null;
    } finally {
      setOlderLoading(false);
    }
  }, [detail, olderLoading, selectedThreadId]);

  React.useEffect(() => {
    const container = timelineRef.current;
    if (!container) return;
    const handleScroll = () => {
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
      timelineNearBottomRef.current = distance <= 72;
    };
    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [open, selectedThreadId]);

  React.useEffect(() => {
    const container = timelineRef.current;
    if (!container || !open || !selectedThreadId) return;
    const preserve = preserveScrollAfterPrependRef.current;
    if (preserve) {
      requestAnimationFrame(() => {
        container.scrollTop = Math.max(0, container.scrollHeight - preserve.height + preserve.top);
        preserveScrollAfterPrependRef.current = null;
      });
      return;
    }
    if (forceScrollToBottomRef.current || timelineNearBottomRef.current) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        timelineNearBottomRef.current = true;
        forceScrollToBottomRef.current = false;
      });
    }
  }, [open, orderedMessages, selectedThreadId]);

  const selectedThreadInQueue = React.useMemo(
    () => !!selectedThreadId && items.some(item => item.id === selectedThreadId),
    [items, selectedThreadId]
  );

  const selectedReplyMessage = React.useMemo(() => {
    if (!replyToMessageId) return null;
    return orderedMessages.find(message => String(message.id || '') === replyToMessageId) || null;
  }, [orderedMessages, replyToMessageId]);

  const syncMentionedJids = React.useCallback((nextBody: string) => {
    setMentionedJids(prev =>
      prev.filter(jid => {
        const label = mentionLabelsRef.current[jid];
        const fallback = normalizePhoneLabel(jid);
        return (label && nextBody.includes(`@${label}`)) || (fallback && nextBody.includes(`@${fallback}`));
      }),
    );
  }, []);

  const handleReplyChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setReply(nextValue);
    setComposerCaretPosition(event.target.selectionStart ?? nextValue.length);
    syncMentionedJids(nextValue);
  }, [syncMentionedJids]);

  const insertMention = React.useCallback((candidate: { jid: string; label: string }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const currentValue = reply || '';
    const caret = textarea.selectionStart ?? composerCaretPosition;
    const beforeCaret = currentValue.slice(0, caret);
    const afterCaret = currentValue.slice(caret);
    const match = beforeCaret.match(/(?:^|\s)@([^\s@]*)$/);
    if (!match || match.index == null) return;
    const prefix = beforeCaret.slice(0, match.index);
    const leadingSpace = match[0].startsWith(' ') ? ' ' : '';
    const insertion = `${leadingSpace}@${candidate.label} `;
    const nextValue = `${prefix}${insertion}${afterCaret}`;
    const nextCaret = (prefix + insertion).length;
    mentionLabelsRef.current[candidate.jid] = candidate.label;
    setReply(nextValue);
    setMentionedJids(prev => (prev.includes(candidate.jid) ? prev : [...prev, candidate.jid]));
    setComposerCaretPosition(nextCaret);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  }, [composerCaretPosition, reply]);

  const sendQuick = React.useCallback(async () => {
    const threadId = selectedThreadId;
    const text = reply.trim();
    if (!threadId) return;
    if (!text && !attachment) return;
    try {
      setSending(true);
      setError('');
      forceScrollToBottomRef.current = true;
      if (attachment) {
        await whatsappService.sendAttachment(threadId, {
          base64Data: attachment.base64Data,
          mimeType: attachment.mimeType,
          fileName: attachment.fileName,
          caption: text || '',
          replyToMessageId: replyToMessageId || null,
          mentionedJids,
        });
      } else {
        await whatsappService.sendMessage(threadId, text, replyToMessageId || null, mentionedJids);
      }
      setReply('');
      setAttachment(null);
      setReplyToMessageId('');
      setMentionedJids([]);
      const selectedUnread = Number(items.find(item => item.id === threadId)?.unreadInboundCount || 0);
      setItems(prev => prev.map(item => item.id === threadId ? { ...item, needsReply: false, isOverdue: false, unreadInboundCount: 0 } : item));
      setUnreadInboundTotal(prev => Math.max(0, prev - selectedUnread));
      try {
        const raw = localStorage.getItem(draftStorageKey);
        const parsed = raw ? JSON.parse(raw) : {};
        const map = parsed && typeof parsed === 'object' ? { ...parsed } : {};
        delete map[threadId];
        localStorage.setItem(draftStorageKey, JSON.stringify(map));
      } catch {
        // ignore storage errors
      }
      await Promise.all([loadQueue(), openDetail(threadId, true)]);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to send quick WhatsApp message.');
    } finally {
      setSending(false);
    }
  }, [attachment, draftStorageKey, items, loadQueue, mentionedJids, openDetail, reply, replyToMessageId, selectedThreadId]);

  const quickSendDisabled = !selectedThreadId || recording || sending || (!reply.trim() && !attachment);
  const handleQuickComposerKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionDropdownOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionActiveIndex(index => Math.min(index + 1, mentionSuggestions.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionActiveIndex(index => Math.max(index - 1, 0));
        return;
      }
      if ((event.key === 'Enter' || event.key === 'Tab') && mentionSuggestions[mentionActiveIndex]) {
        event.preventDefault();
        insertMention(mentionSuggestions[mentionActiveIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setComposerCaretPosition(-1);
        return;
      }
    }
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (quickSendDisabled) return;
    void sendQuick();
  }, [insertMention, mentionActiveIndex, mentionDropdownOpen, mentionSuggestions, quickSendDisabled, sendQuick]);

  const handleAttachmentChange = React.useCallback(async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!allowedAttachment(file)) {
      setError(`Unsupported file type: ${file.name}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setError(`Attachment too large: ${file.name}. Max 16 MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      setError('');
      const base64Data = await fileToBase64(file);
      setAttachment({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64Data,
        fileSizeBytes: file.size,
      });
    } catch {
      setError(`Failed to read attachment: ${file.name}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  const startRecording = React.useCallback(async () => {
    if (recording || sending || statusBusy || transferBusy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const preferredMimeTypes = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm'];
      const selectedMimeType = preferredMimeTypes.find(type => {
        try {
          return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
        } catch {
          return false;
        }
      });
      const recorder = selectedMimeType ? new MediaRecorder(stream, { mimeType: selectedMimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      mediaChunksRef.current = [];
      setRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = window.setInterval(() => setRecordingSeconds(prev => prev + 1), 1000);

      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) mediaChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const chunks = mediaChunksRef.current;
        mediaChunksRef.current = [];
        setRecording(false);
        stopMediaStream();
        if (!chunks.length) return;
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type: mimeType });
        if (!blob.size) return;
        const extension = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `voice-note-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`, { type: mimeType });
        void (async () => {
          try {
            const base64Data = await fileToBase64(file);
            setAttachment({
              fileName: file.name,
              mimeType: file.type || 'audio/webm',
              base64Data,
              fileSizeBytes: file.size,
            });
            setError('');
          } catch {
            setError('Failed to process voice memo recording.');
          }
        })();
      };

      recorder.start();
    } catch (e: any) {
      stopMediaStream();
      setRecording(false);
      setError(e?.message || 'Microphone permission denied or unavailable.');
    }
  }, [recording, sending, statusBusy, stopMediaStream, transferBusy]);

  const stopRecording = React.useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    try {
      recorder.stop();
    } catch {
      setRecording(false);
      stopMediaStream();
    }
  }, [stopMediaStream]);

  const cancelRecording = React.useCallback(() => {
    mediaChunksRef.current = [];
    setRecording(false);
    stopMediaStream();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.onstop = null;
        recorder.stop();
      } catch {}
    }
    mediaRecorderRef.current = null;
  }, [stopMediaStream]);

  const recordingLabel = React.useMemo(() => {
    const mins = Math.floor(recordingSeconds / 60);
    const secs = recordingSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, [recordingSeconds]);

  const updateStatus = React.useCallback(async (status: 'OPEN' | 'WAITING_CLIENT' | 'RESOLVED_WON' | 'RESOLVED_LOST') => {
    if (!selectedThreadId) return;
    try {
      setStatusBusy(true);
      setError('');
      await whatsappService.updateThreadStatus(selectedThreadId, status, null);
      await Promise.all([loadQueue(), openDetail(selectedThreadId, true)]);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to update status.');
    } finally {
      setStatusBusy(false);
    }
  }, [loadQueue, openDetail, selectedThreadId]);

  const transferThread = React.useCallback(async () => {
    if (!selectedThreadId || !transferUserId) return;
    try {
      setTransferBusy(true);
      setError('');
      await whatsappService.assignThread(selectedThreadId, transferUserId, 'Transferred from quick drawer');
      setTransferUserId('');
      await Promise.all([loadQueue(), openDetail(selectedThreadId, true)]);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to transfer thread.');
    } finally {
      setTransferBusy(false);
    }
  }, [loadQueue, openDetail, selectedThreadId, transferUserId]);

  const markSelectedUnread = React.useCallback(async () => {
    if (!selectedThreadId) return;
    try {
      setStatusBusy(true);
      setError('');
      manualUnreadGuardRef.current[selectedThreadId] = Date.now();
      await whatsappService.markThreadRead(selectedThreadId, {
        lastReadAt: new Date(0).toISOString(),
        lastReadMessageId: null,
      });
      setDetail(prev => prev && prev.id === selectedThreadId
        ? { ...prev, lastReadAt: null, lastReadMessageId: null, needsReply: true }
        : prev
      );
      setItems(prev => prev.map(item => item.id === selectedThreadId
        ? { ...item, unreadInboundCount: Math.max(1, Number(item.unreadInboundCount || 0)), needsReply: true }
        : item
      ));
      setUnreadInboundTotal(prev => Math.max(prev, 1));
      await loadQueue();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to mark WhatsApp thread unread.');
    } finally {
      setStatusBusy(false);
    }
  }, [loadQueue, selectedThreadId]);

  if (!currentUserId) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`fixed bottom-6 right-6 z-[130] inline-flex h-16 w-16 items-center justify-center rounded-3xl border border-indigo-900/60 bg-[#0a1430] text-white shadow-[0_18px_36px_rgba(2,8,23,0.42)] transition hover:scale-[1.02] ${pulse ? 'animate-pulse' : ''}`}
        aria-label="Open quick WhatsApp chat"
      >
        <MessageCircle className="h-7 w-7" />
        {displayNeedsReplyCount > 0 ? (
          <span className="absolute right-2 top-2 inline-flex min-w-[18px] items-center justify-center rounded-full bg-indigo-400 px-1 text-[10px] font-black text-white">
            {displayNeedsReplyCount > 9 ? '9+' : displayNeedsReplyCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[125] flex items-end justify-end bg-black/25 p-4 md:items-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex h-[85vh] w-full max-w-[1020px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_40px_80px_rgba(2,8,23,0.35)]"
            onClick={event => event.stopPropagation()}
          >
            <aside className="flex w-[300px] shrink-0 flex-col border-r border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Quick WhatsApp</div>
                  <div className="text-sm font-bold text-slate-800">{displayNeedsReplyCount} need reply</div>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 text-slate-500 hover:bg-slate-200">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
                <button
                  type="button"
                  onClick={() => void loadQueue()}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600"
                >
                  <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <span className={`rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${sseConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {sseConnected ? 'Live' : 'Fallback'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const target = selectedThreadId || items[0]?.id;
                    onOpenFullHub?.(target || undefined);
                    setOpen(false);
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  Full Hub
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                {!selectedThreadInQueue && selectedThreadId && detail ? (
                  <div className="mb-2 rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2">
                    <div className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">Current Chat</div>
                    <div className="mt-0.5 truncate text-sm font-black text-slate-800">
                      {getThreadDisplayName(detail)}
                    </div>
                    <div className="mt-1 text-[10px] font-semibold text-slate-500">
                      Not in needs-reply list after your outbound reply.
                    </div>
                  </div>
                ) : null}
                {items.length === 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-xs text-slate-500">No urgent threads right now.</div>
                ) : items.map(item => {
                  const active = selectedThreadId === item.id;
                  const wait = minutesSince(item.lastInboundAt);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedThreadId(item.id)}
                      className={`mb-2 w-full rounded-xl border px-3 py-2 text-left ${active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    >
                      <div className="truncate text-sm font-black text-slate-800">{getThreadDisplayName(item)}</div>
                      <div className="mt-1 line-clamp-2 text-xs text-slate-600">{whatsappMessagePreview(item.lastMessage)}</div>
                      <div className="mt-1 text-[10px] font-semibold text-slate-400">
                        {item.isOverdue ? `Overdue${wait !== null ? ` · ${wait}m` : ''}` : item.needsReply ? 'Needs reply' : 'Active'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="truncate text-sm font-black text-slate-800">
                  {detail ? getThreadDisplayName(detail) : 'Select a thread'}
                </div>
                {activeTab !== 'INBOX' ? (
                  <button
                    type="button"
                    onClick={() => {
                      const target = selectedThreadId || items[0]?.id;
                      onOpenFullHub?.(target || undefined);
                      setOpen(false);
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open Hub
                  </button>
                ) : null}
              </div>

              {selectedThreadId ? (
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Quick Actions</span>
                  <select
                    value={String(detail?.status || 'OPEN')}
                    onChange={event => void updateStatus(event.target.value as any)}
                    disabled={statusBusy}
                    className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600"
                  >
                    <option value="OPEN">Open</option>
                    <option value="WAITING_CLIENT">Waiting</option>
                    <option value="RESOLVED_WON">Won</option>
                    <option value="RESOLVED_LOST">Lost</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => void whatsappService.assignThread(selectedThreadId).then(async () => {
                      await Promise.all([loadQueue(), openDetail(selectedThreadId, true)]);
                    }).catch(() => setError('Failed to claim thread.'))}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700"
                  >
                    <UserCheck className="h-3 w-3" />
                    Claim
                  </button>
                  <button
                    type="button"
                    onPointerDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={() => void markSelectedUnread()}
                    disabled={statusBusy}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600 disabled:opacity-50"
                  >
                    <Mail className="h-3 w-3" />
                    Unread
                  </button>
                  {isLeadership ? (
                    <>
                      <select
                        value={transferUserId}
                        onChange={event => setTransferUserId(event.target.value)}
                        className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600"
                      >
                        <option value="">Transfer to...</option>
                        {waAssignableUsers
                          .filter(user => user.id !== detail?.assignedUserId)
                          .map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => void transferThread()}
                        disabled={!transferUserId || transferBusy}
                        className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-sky-700 disabled:opacity-50"
                      >
                        {transferBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : null}
                        Transfer
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              <div ref={timelineRef} className="min-h-0 flex-1 overflow-y-auto bg-slate-100 px-4 py-3">
                {!selectedThreadId ? (
                  <div className="text-sm text-slate-500">Select a conversation to quick-reply.</div>
                ) : detailLoading && !detail ? (
                  <div className="text-sm text-slate-500">Loading conversation...</div>
                ) : (
                  <div className="space-y-2">
                    {canLoadOlderMessages ? (
                      <div className="flex justify-center pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            onOpenFullHub?.(selectedThreadId);
                            setOpen(false);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 shadow-sm"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Older history in full hub
                        </button>
                      </div>
                    ) : null}
                    {orderedMessages.map(message => {
                      const outbound = String(message.direction || '').toUpperCase() === 'OUTBOUND';
                      const resolvedBody = resolveMentionText((message as any).resolvedBody || message.body || '');
                      const body = resolvedBody || whatsappMessagePreview(message as any);
                      const hasBody = !!resolvedBody.trim();
                      return (
                        <div key={message.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${outbound ? 'bg-emerald-600 text-white' : 'bg-white text-slate-800 border border-slate-200'}`}>
                            <QuickMediaPreview message={message} outbound={outbound} onOpenMedia={setMediaPreview} />
                            {hasBody ? (
                              <div className="whitespace-pre-wrap">{renderWhatsAppFormattedText(resolvedBody)}</div>
                            ) : (
                              <div className={`${message.mediaUrl ? 'sr-only' : ''} whitespace-pre-wrap`}>{body}</div>
                            )}
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <div className={`text-[10px] ${outbound ? 'text-emerald-100' : 'text-slate-400'}`}>
                                {new Date(getMessageAt(message) || '').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <button
                                type="button"
                                onClick={() => setReplyToMessageId(String(message.id || ''))}
                                className={`rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${outbound ? 'border-emerald-300/60 bg-emerald-500/20 text-white' : 'border-slate-300 bg-slate-50 text-slate-500'}`}
                              >
                                Reply
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 bg-white p-3">
                {error ? <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
                {selectedReplyMessage ? (
                  <div className="mb-2 flex items-start justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700">Replying To</div>
                      <div className="truncate text-xs text-slate-700">
                        {whatsappMessagePreview(selectedReplyMessage as any)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyToMessageId('')}
                      className="rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-700"
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
                {attachment ? (
                  <div className="mb-2 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    <span className="truncate">{attachment.fileName} ({Math.max(1, Math.round(attachment.fileSizeBytes / 1024))} KB)</span>
                    <button type="button" onClick={() => setAttachment(null)} className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">
                      Remove
                    </button>
                  </div>
                ) : null}
                {recording ? (
                  <div className="mb-2 flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    <span className="font-semibold">Recording voice memo... {recordingLabel}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={cancelRecording} className="rounded-full border border-rose-300 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-rose-700">
                        Cancel
                      </button>
                      <button type="button" onClick={stopRecording} className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-700">
                        Stop
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="relative flex items-end gap-2">
                  {mentionDropdownOpen ? (
                    <div className="absolute bottom-full left-0 z-10 mb-2 max-h-64 w-[min(360px,calc(100vw-120px))] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_18px_40px_rgba(15,23,42,0.22)]">
                      <div className="px-2 pb-1 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Mention group member</div>
                      {mentionSuggestions.map((candidate, index) => (
                        <button
                          key={candidate.jid}
                          type="button"
                          onMouseDown={event => {
                            event.preventDefault();
                            insertMention(candidate);
                          }}
                          className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left ${index === mentionActiveIndex ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                            {candidate.label.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-black text-slate-800">{candidate.label}</div>
                            <div className="truncate text-xs font-semibold text-slate-500">{candidate.subtitle}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <textarea
                    ref={textareaRef}
                    rows={2}
                    value={reply}
                    onChange={handleReplyChange}
                    onSelect={event => setComposerCaretPosition(event.currentTarget.selectionStart ?? 0)}
                    onClick={event => setComposerCaretPosition(event.currentTarget.selectionStart ?? 0)}
                    onKeyDown={handleQuickComposerKeyDown}
                    placeholder="Quick WhatsApp reply..."
                    className="min-h-[42px] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-400"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                    className="hidden"
                    onChange={event => void handleAttachmentChange(event.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600"
                    title="Attach file"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void startRecording()}
                    disabled={recording || sending}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 disabled:opacity-40"
                    title="Record voice memo"
                  >
                    {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => void sendQuick()}
                    disabled={quickSendDisabled}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-white shadow-[0_8px_18px_rgba(5,150,105,0.35)] disabled:opacity-40"
                  >
                    {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {mediaPreview ? (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/80 p-4" onClick={() => setMediaPreview(null)}>
          <div className="relative max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-slate-950 p-3 shadow-2xl" onClick={event => event.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="truncate text-sm font-bold text-white">{mediaPreview.title}</div>
              <button type="button" onClick={() => setMediaPreview(null)} className="rounded-full border border-white/20 bg-white/10 p-2 text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            {mediaPreview.kind === 'image' ? (
              <img src={mediaPreview.url} alt={mediaPreview.title} className="mx-auto max-h-[82vh] max-w-full rounded-xl object-contain" />
            ) : mediaPreview.kind === 'video' ? (
              <video src={mediaPreview.url} controls className="mx-auto max-h-[82vh] max-w-full rounded-xl bg-black" />
            ) : mediaPreview.kind === 'audio' ? (
              <div className="rounded-xl bg-white p-4">
                <audio src={mediaPreview.url} controls className="w-full" />
              </div>
            ) : (
              <a href={mediaPreview.url} target="_blank" rel="noreferrer" className="block rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-800">
                Open document
              </a>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default GlobalWhatsAppQuickDrawer;
